/**
 * quote-math — 견적 계산 순수 함수 (DealDetailView 에서 추출, 2026-07-02)
 *
 * ─── 마진율 정의 (정본) ────────────────────────────────────────────────
 * 마진율 = 이익 / **판매가** 기준.
 *   견적단가 = 원가 ÷ (1 − 마진율/100)
 *   예: 원가 100, 마진율 30% → 견적단가 143 (= 100/0.7 반올림)
 *       검산: (143 − 100) / 143 ≈ 30%
 *
 * ⚠ 원가 기준 마크업(원가 × (1 + 마진율/100))이 **아님**.
 *   초기 구현이 마크업 방식이었다가 커밋 83cb1ab 에서 판매가 기준으로 정정됨.
 *   이 정의는 quote-math.test.ts 가 동결한다 — 공식을 바꾸려면 테스트부터 바꿀 것.
 *
 * 이 파일은 DOM / api / Sola 에 의존하지 않는다 (테스트 가능성의 조건).
 * DealDetailView 의 QuoteModalRow / QuoteModalState 는 아래 *Like 인터페이스를
 * 구조적으로 만족하므로 그대로 넘길 수 있다.
 */

/** 원가 스냅샷을 가진 행 (QuoteModalRow 의 부분집합) */
export interface CostBasisLike {
  costBasisAmount:   string;        // native 금액 문자열. '' = 없음
  costBasisCurrency: string | null; // 'USD' | 'KRW' | null
}

/** 합계 계산에 필요한 행 (QuoteModalRow 의 부분집합) */
export interface QuoteRowLike extends CostBasisLike {
  quantity:       string;
  unit_price_krw: string;
  /** 서버 그룹. 미지정이면 평면 행으로 취급(기존 견적 무영향). */
  lineType?:         'server' | 'part';
  parentDealItemId?: string | null;
  deal_item_id?:     string | null;
  serverQuantity?:   number | null;
  /**
   * 기본 구성으로 자동으로 담긴 행인가 (마이그 174).
   * 원가 0 은 사람이 판단한 값이 아니라 시스템이 넣은 값이라
   * anyCostEntered 판정에서 뺀다 — 아래 그 함수 주석 참조.
   */
  isBasePart?:       boolean;
}

/** 합계 계산 입력 (QuoteModalState 의 부분집합) */
export interface QuoteTotalsInput {
  fxRate:      string;          // 환율 KRW/USD 문자열
  vatIncluded: boolean;
  items:       readonly QuoteRowLike[];
}

export interface QuoteTotals {
  costBasisKrw:  number;        // Σ (매입단가 KRW × 수량) — 원가 있는 행만
  totalKrw:      number;        // Σ (판매단가 KRW × 수량)
  amountWithVat: number;        // 부가세 포함 KRW (vatIncluded ? 그대로 : ×1.1 반올림)
  marginKrw:     number | null; // 판매 − 매입 (모든 행에 매입단가 있고 totalKrw>0 일 때)
  marginRate:    number | null; // 전체 마진율 % = (판매−매입)/판매×100
  costComplete:  boolean;       // 모든 행이 매입단가를 가지면 true. false → 매입합계/마진 '확인 필요'
}

/** 숫자 문자열 → number. 빈/비수치 는 null. */
export function parseNum(s: string): number | null {
  if (!s || !s.trim()) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** 숫자 문자열 → 반올림 정수. 빈/비수치 는 null. */
export function parseInt0(s: string): number | null {
  const n = parseNum(s);
  return n == null ? null : Math.round(n);
}

/**
 * 이 견적에서 **미입력 매입단가를 0 으로 볼 것인가** (2026-08 서안드레 스펙).
 *
 * 규칙:
 *   - 매입단가가 **하나도** 입력되지 않았으면 false → 예전과 같이 전부 '확인 필요'.
 *   - **하나라도** 입력됐으면 true → 나머지 미입력 칸은 0 원으로 보고 합계·마진을 낸다.
 *
 * 0 도 '입력'으로 친다. 안 넣은 칸(`''`)과 0 을 넣은 칸(`'0'`)은 state 에서
 * 구분되며, 후자는 "0 원으로 확정" 이라는 사람의 판단이다.
 *
 * 서버 헤더행은 자기 원가 칸이 없다(자식 합계에서 유도) — 판정에서 뺀다.
 *
 * ★ 기본 구성 행(isBasePart)도 뺀다. 위 "0 = 사람의 판단" 이라는 전제가 이 행에는
 *   성립하지 않는다 — 마이그 174(2026-08-12, 이 규칙 다음 날)가 들어오면서
 *   기본 구성 행은 담기는 순간 cost_basis_amount=0 이 **자동으로** 박힌다
 *   (ServerConfigView.baseRowMarks). 빼지 않으면 사람이 매입단가를 하나도 넣지
 *   않았는데 스위치가 켜져, 비어 있는 CHASSIS 원가까지 0 으로 쳐서 마진율이
 *   100% 로 보인다. 이 행 자체는 여전히 원가 0 확정이며, 그건 호출부가
 *   `zeroCost || isBasePart` 로 행 단위로 넘긴다.
 *
 * ⚠ 이 모드에서도 **환산 불가는 0 이 되지 않는다.** 외화 금액이 입력됐는데
 *   환율이 없으면 원가는 여전히 '불명' 이다(rowCostKrw 가 null 을 준다).
 *   불명을 0 으로 치면 마진이 조용히 부풀어 오른다.
 */
export function anyCostEntered(items: readonly QuoteRowLike[]): boolean {
  return items.some((r) => r.lineType !== 'server'
                        && !r.isBasePart
                        && parseNum(r.costBasisAmount) != null);
}

/**
 * 소스 스냅샷(native 통화) → 원가 KRW 단가.
 * KRW 는 그대로 반올림, 그 외 통화는 fx(KRW/USD) 환산. 환율 없으면(≤0) null.
 *
 * `missingIsZero` — anyCostEntered() 가 true 인 견적에서만 켠다.
 *   **금액 칸이 비어 있을 때만** 0 을 돌려준다. 금액은 있는데 환산이 안 되는
 *   경우(외화 + 환율 없음)는 여전히 null 이다.
 */
export function rowCostKrw(row: CostBasisLike, fx: number, missingIsZero = false): number | null {
  const amt = parseNum(row.costBasisAmount);
  if (amt == null) return missingIsZero ? 0 : null;
  if (row.costBasisCurrency === 'KRW') return Math.round(amt);
  return fx > 0 ? Math.round(amt * fx) : null;
}

/**
 * 견적단가(개당, KRW) = 원가 ÷ (1 − 마진율/100), 반올림.
 * 마진율 ≥ 100% 는 정의 불능(0 나눗셈/음수 판매가) → null.
 */
export function computeSellUnitKrw(costKrw: number, marginRatePct: number): number | null {
  const denom = 1 - marginRatePct / 100;
  return denom > 0 ? Math.round(costKrw / denom) : null;
}

/**
 * 마진율 역산 % = (판매가 − 원가) / 판매가 × 100, 소수 1자리 반올림.
 * 판매가 ≤ 0 은 정의 불능 → null. (음수 = 역마진 — 클램프하지 않고 그대로 반환,
 * '0' 표시 여부는 뷰의 결정)
 */
export function backCalcMarginRate(costKrw: number, sellUnitKrw: number): number | null {
  if (sellUnitKrw <= 0) return null;
  const m = (sellUnitKrw - costKrw) / sellUnitKrw * 100;
  return Math.round(m * 10) / 10;
}

// ─── 한 행 마진 (생성/수정 모달·보기 모달 공용 — 단일 진실 소스) ──────────
export interface RowMargin {
  costPresent: boolean;       // 원가가 확정됐는가. 기본은 '> 0', zeroIsCost 면 0 도 확정
  lineCost:    number | null; // 매입단가 합계 (costPresent 일 때만)
  lineSell:    number | null; // 판매단가 합계 (판매단가 있을 때만)
  marginAmt:   number | null; // 마진 금액 = 판매합계 − 매입합계 (매입단가 있을 때만)
  marginRate:  number | null; // 마진율 % (매입단가 없음/0 → null = '확인 필요')
}

/**
 * 한 견적 행의 마진 계산 — 두 모달(생성/수정·보기)이 공유하는 유일 로직.
 *   판매단가 합계 0/없음이면 마진율 0%.
 *
 * `zeroIsCost` (= anyCostEntered 결과):
 *   false — 예전 규칙. 매입단가가 없거나 0 이면 '확인 필요'.
 *   true  — 0 도 확정된 원가로 본다. 미입력 칸은 호출부가 이미 0 으로 넘긴다.
 */
export function computeRowMargin(
  costKrw:     number | null,
  sellUnitKrw: number | null,
  qty:         number,
  zeroIsCost = false,
): RowMargin {
  const costPresent = costKrw != null && (zeroIsCost || costKrw > 0);
  const lineCost = costPresent ? costKrw! * qty : null;
  const lineSell = sellUnitKrw != null ? sellUnitKrw * qty : null;
  const marginAmt = costPresent && lineSell != null ? lineSell - lineCost! : null;
  const marginRate = !costPresent
    ? null
    : (lineSell == null || lineSell === 0
        ? 0
        : (backCalcMarginRate(costKrw!, sellUnitKrw!) ?? 0));
  return { costPresent, lineCost, lineSell, marginAmt, marginRate };
}

/**
 * 여러 행(RowMargin) → 견적 합계. computeQuoteTotals 및 보기 모달이 공유.
 *   costPresent 가 아닌 행이 하나라도 있으면 costComplete=false → 매입합계·마진 '확인 필요'.
 *   "0 을 원가로 볼지" 는 이미 computeRowMargin 이 정했다(anyCostEntered).
 */
export function summarizeRows(rows: RowMargin[], vatIncluded: boolean): QuoteTotals {
  let costBasisKrw = 0;
  let totalKrw     = 0;
  let costComplete = rows.length > 0;
  for (const r of rows) {
    totalKrw += r.lineSell ?? 0;
    if (r.costPresent) costBasisKrw += r.lineCost!;
    else costComplete = false;
  }
  const amountWithVat = vatIncluded ? totalKrw : Math.round(totalKrw * 1.1);
  // 마진은 **매입이 정해졌으면** 낸다. 판매합계가 0 이어도 막지 않는다.
  //   예전엔 `totalKrw > 0` 조건이 더 있어서, 매입만 넣고 판매단가를 아직
  //   안 넣은 견적이 매입합계는 보이는데 마진만 '확인 필요' 로 남았다.
  //   그 상태의 마진은 실제로 '전액 손실'(= −매입합계)이고, 그걸 보여줘야
  //   숫자를 맞춰 나갈 수 있다. 음수도 그대로 낸다(클램프 금지).
  //
  //   마진율만은 판매합계가 0 이면 정의되지 않는다(0 으로 나눈다). 이때 0% 로
  //   두는 것은 행 단위 규칙(computeRowMargin: lineSell === 0 → 0)과 같다 —
  //   한 화면에서 행과 합계가 서로 다른 규칙을 쓰면 안 된다.
  const marginKrw  = costComplete ? totalKrw - costBasisKrw : null;
  const marginRate = !costComplete
    ? null
    : (totalKrw > 0 ? (totalKrw - costBasisKrw) / totalKrw * 100 : 0);
  return { costBasisKrw, totalKrw, amountWithVat, marginKrw, marginRate, costComplete };
}

/**
 * 견적 합계 — 매입/판매/부가세/마진.
 * 판매합계(totalKrw)·VAT 는 항상 판매단가로 계산.
 * 매입단가가 하나라도 비면 costComplete=false → 매입합계·전체마진율은 '확인 필요'.
 */
export function computeQuoteTotals(m: QuoteTotalsInput): QuoteTotals {
  const fx = parseNum(m.fxRate) ?? 0;
  // 매입단가가 하나라도 입력됐으면 나머지 미입력 칸은 0 으로 본다(서안드레 스펙).
  // 하나도 없으면 예전 그대로 전부 '확인 필요'.
  const zeroCost = anyCostEntered(m.items);
  // 각 행을 공용 computeRowMargin 으로 정규화 후 summarizeRows 로 집계(단일 로직).
  //
  // 서버가 있으면 **최상위 행만** 합계에 넣는다. 고객 총액은 언제나
  // `번들가 × 대수` 이고, 구성품 판매가는 참고값이라 총액·마진을 바꾸지 않는다
  // (HANDOFF 소유표 / CONFIG2-004). 구성품까지 더하면 같은 물건이 두 번 계산된다.
  // 서버가 없는 견적에서는 필터가 아무것도 걸러내지 않아 기존 동작 그대로다.
  const rows = m.items
    .filter((row) => !isServerChild(row))
    .map((row) => computeRowMargin(
      serverRowCostKrw(row, m.items, fx, zeroCost),
      serverEffectiveSellKrw(row, m.items),   // 서버 헤더는 자식 합(자동)/수동값
      parseInt0(row.quantity) ?? 0,
      zeroCost,
    ));
  return summarizeRows(rows, m.vatIncluded);
}

/**
 * 견적 생성을 막아야 하는 사유 — 없으면 빈 배열.
 *
 * 서버 때문에 막는 경우는 **정확히 둘뿐**이다:
 *   1) 서버 1대 판매가 미입력 — Config 에선 선택값이지만 견적에선 필수다
 *      (CONFIG2-004 / QUOTE-008). 자식 합계를 자동 승격하지 않으므로(마이그 146)
 *      비어 있으면 고객 총액을 만들 수 없다.
 *   2) 구성품 판매가가 **하나라도** 있는데 그 합계가 서버 1대 판매가와 다름 —
 *      숫자가 서로 어긋난 채 고객에게 나가는 걸 막는다.
 *
 * 그 외에는 막지 않는다. 특히 **구성품 판매가가 전부 비어 있으면 통과**한다 —
 * 구성품 판매가는 원래 선택 입력이고, 고객 총액은 번들가 × 대수로만 정해진다.
 * 일부만 채운 경우도 별도 사유로 두지 않는다. 채운 것들의 합이 번들가와 같으면
 * 그대로 통과하고, 다르면 2번 하나로 걸린다(사유가 늘면 어디를 고쳐야 하는지
 * 오히려 흐려진다).
 *
 * 반환값은 저장 버튼 비활성 + 안내 문구에 쓴다. alert 로 튕기지 않는 이유는
 * 사용자가 어느 줄을 고쳐야 하는지 보면서 맞춰야 하기 때문이다.
 */
export function quoteBlockReasons(items: readonly QuoteRowLike[]): string[] {
  const out: string[] = [];

  for (const s of items.filter((r) => r.lineType === 'server')) {
    // 자동(자식 합)이든 수동이든 실제로 쓰는 값으로 본다. 자동일 때는 자식
    //   합이 곧 bundle 이라 아래 불일치 검사에서 sum === bundle 로 통과한다.
    const bundle = serverEffectiveSellKrw(s, items);
    if (bundle == null || bundle <= 0) {
      out.push('서버 1대 판매가를 입력해 주세요.');
      continue;
    }
    const priced = items.filter((r) => r.parentDealItemId != null
      && s.deal_item_id != null
      && String(r.parentDealItemId) === String(s.deal_item_id)
      && parseInt0(r.unit_price_krw) != null);
    if (priced.length === 0) continue;   // 전부 비었으면 검증 대상이 아니다

    const sum = priced.reduce(
      (acc, k) => acc + (parseInt0(k.unit_price_krw) ?? 0) * (parseInt0(k.quantity) ?? 0), 0);
    if (sum !== bundle) {
      out.push(`구성품 판매가 합계(${sum.toLocaleString('ko-KR')})가 `
        + `서버 1대 판매가(${bundle.toLocaleString('ko-KR')})와 맞지 않습니다.`);
    }
  }
  return out;
}

/** 서버 구성품(부모가 있는 행)인가 — 합계에서 제외할 근거. */
export function isServerChild(row: QuoteRowLike): boolean {
  return row.parentDealItemId != null;
}

/**
 * 행의 매입 단가(개당 KRW).
 *
 * 서버 헤더행에는 자기 원가가 없다(소스 칸이 `-`). 대신 **자식 구성품의
 * 매입 합계**가 서버 1대분 원가다.
 *
 * `missingIsZero=false` (기본): 자식 하나라도 원가가 없으면 null → `확인 필요`.
 * `missingIsZero=true`: 미입력 자식을 0 으로 보고 합산한다. 단 환산 불가
 *   (외화 + 환율 없음)는 여전히 null 이다 — 불명을 0 으로 치면 마진이 부푼다.
 */
export function serverRowCostKrw(
  row:   QuoteRowLike,
  all:   readonly QuoteRowLike[],
  fx:    number,
  missingIsZero = false,
): number | null {
  if (row.lineType !== 'server') return rowCostKrw(row, fx, missingIsZero);

  const kids = all.filter((r) => r.parentDealItemId != null
    && row.deal_item_id != null
    && String(r.parentDealItemId) === String(row.deal_item_id));
  if (kids.length === 0) return null;

  let sum = 0;
  for (const k of kids) {
    const unit = rowCostKrw(k, fx, missingIsZero);
    // 환산 불가(null)는 두 모드 모두 '확인 필요'. 0 은 모드에 따라 갈린다.
    if (unit == null || (!missingIsZero && unit <= 0)) return null;
    sum += unit * (parseInt0(k.quantity) ?? 0);
  }
  return missingIsZero ? sum : (sum > 0 ? sum : null);
}

/**
 * 서버 헤더행의 자식 판매가 합 — 자동 판매단가(구성 페이지 mirroredBundle 규칙).
 *
 * 자식 중 **하나라도** 판매가가 입력됐으면 나머지 미입력 칸은 0 으로 보고 합한다.
 * 하나도 없으면 채울 값이 없으므로 null(자동 불가 → 사람이 직접 넣어야 한다).
 *
 * 서버 1대 기준 값이다 — 자식 판매단가(개당) × 자식 수량(서버 1대당)의 합.
 * 서버 대수는 여기서 곱하지 않는다(판매단가 합계에서 곱한다).
 */
export function serverChildSellSum(
  row: QuoteRowLike,
  all: readonly QuoteRowLike[],
): number | null {
  if (row.lineType !== 'server') return null;
  const kids = all.filter((r) => r.parentDealItemId != null
    && row.deal_item_id != null
    && String(r.parentDealItemId) === String(row.deal_item_id));
  if (kids.length === 0) return null;
  const anyPriced = kids.some((k) => parseInt0(k.unit_price_krw) != null);
  if (!anyPriced) return null;
  return kids.reduce(
    (acc, k) => acc + (parseInt0(k.unit_price_krw) ?? 0) * (parseInt0(k.quantity) ?? 0), 0);
}

/**
 * 서버 헤더행이 **실제로 쓰는** 판매단가(서버 1대 판매가).
 *
 *   수동값이 있으면(unit_price_krw 입력됨) 그 값 — 사람이 정한 값은 자동이 덮지 않는다.
 *   비어 있으면 자식 합(serverChildSellSum) — 자동.
 *
 * 플래그 없이 '칸이 비었나' 하나로 자동/수동을 가른다. 비어 있음=자동이므로 자식
 * 판매가가 바뀌면 다음 렌더에서 따라 바뀌고, 값이 있으면 수동이라 그대로 둔다.
 * 수동값과 자식 합이 다르면 quoteBlockReasons 가 불일치로 잡는다.
 *
 * 서버 헤더가 아니면 그냥 자기 판매단가.
 */
export function serverEffectiveSellKrw(
  row: QuoteRowLike,
  all: readonly QuoteRowLike[],
): number | null {
  if (row.lineType !== 'server') return parseInt0(row.unit_price_krw);
  const manual = parseInt0(row.unit_price_krw);
  return manual != null ? manual : serverChildSellSum(row, all);
}
